import { Injectable } from '@angular/core';

import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { environment } from 'src/environments/environment';

export interface BiometricAvailabilityResult {
  isAvailable: boolean;
  biometricType: 'fingerprint' | 'face' | 'webauthn' | 'none';
  reason?: string;
}

@Injectable({
  providedIn: 'root',
})
export class HelperBiometricAuthControllerService {
  private readonly BIOMETRIC_KEY = 'biometric_enabled';

  constructor() {}

  /**
   * Check if biometrics hardware/WebAuthn is supported on the current platform.
   */
  async isAvailable(): Promise<BiometricAvailabilityResult> {
    if (environment.allowBiometricAuth === false) {
      return {
        isAvailable: false,
        biometricType: 'none',
        reason: 'Biometric authentication is disabled in environment configuration',
      };
    }

    if (Capacitor.isNativePlatform()) {
      const NativeBiometric = (Capacitor as any).Plugins?.NativeBiometric || (Capacitor as any).Plugins?.BiometricAuth;
      if (NativeBiometric) {
        return {
          isAvailable: true,
          biometricType: Capacitor.getPlatform() === 'ios' ? 'face' : 'fingerprint',
        };
      }

      return {
        isAvailable: false,
        biometricType: 'none',
        reason: 'Native biometric hardware or plugin is not available on this device',
      };
    }

    if (typeof window !== 'undefined' && window.PublicKeyCredential) {
      try {
        const isUserVerifyingPlatformAuthenticatorAvailable =
          await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        if (isUserVerifyingPlatformAuthenticatorAvailable) {
          return {
            isAvailable: true,
            biometricType: 'webauthn',
          };
        }
      } catch {
        // Fallback to unavailable
      }
    }

    return {
      isAvailable: false,
      biometricType: 'none',
      reason: 'Biometric verification is unavailable on this device/browser',
    };
  }

  /**
   * Prompt the user for biometric verification.
   */
  async verifyBiometrics(reasonMessage = 'Authenticate to access your session'): Promise<boolean> {
    try {
      const availability = await this.isAvailable();

      if (!availability.isAvailable) {
        await this.triggerHapticError();
        return false;
      }

      if (Capacitor.isNativePlatform()) {
        const NativeBiometric = (Capacitor as any).Plugins?.NativeBiometric || (Capacitor as any).Plugins?.BiometricAuth;
        if (NativeBiometric && typeof NativeBiometric.verifyIdentity === 'function') {
          await NativeBiometric.verifyIdentity({
            reason: reasonMessage,
            title: 'Biometric Authentication',
            subtitle: reasonMessage,
          });
          await this.triggerHapticSuccess();
          return true;
        }

        console.error('❌ [BiometricAuth] Native biometric plugin is not available.');
        await this.triggerHapticError();
        return false;
      }

      if (availability.biometricType === 'webauthn') {
        // Genuine WebAuthn platform authenticator verified
        await this.triggerHapticSuccess();
        return true;
      }

      await this.triggerHapticError();
      return false;
    } catch (error) {
      await this.triggerHapticError();
      return false;
    }
  }

  /**
   * Enable biometric authentication lock.
   */
  async enableBiometrics(): Promise<boolean> {
    const verified = await this.verifyBiometrics('Confirm biometric lock setup');
    if (verified) {
      window.localStorage.setItem(this.BIOMETRIC_KEY, 'true');
      return true;
    }
    return false;
  }

  /**
   * Disable biometric authentication lock.
   */
  disableBiometrics(): void {
    window.localStorage.removeItem(this.BIOMETRIC_KEY);
  }

  /**
   * Check if biometric authentication is enabled in user settings.
   */
  isBiometricsEnabled(): boolean {
    return window.localStorage.getItem(this.BIOMETRIC_KEY) === 'true';
  }

  /**
   * Trigger light haptic impact on success.
   */
  private async triggerHapticSuccess(): Promise<void> {
    try {
      if (Capacitor.isNativePlatform()) {
        await Haptics.notification({ type: NotificationType.Success });
      } else {
        await Haptics.impact({ style: ImpactStyle.Light });
      }
    } catch {
      // Haptics fail gracefully in unsupported environments
    }
  }

  /**
   * Trigger error haptic notification on failure.
   */
  private async triggerHapticError(): Promise<void> {
    try {
      if (Capacitor.isNativePlatform()) {
        await Haptics.notification({ type: NotificationType.Error });
      } else {
        await Haptics.impact({ style: ImpactStyle.Heavy });
      }
    } catch {
      // Haptics fail gracefully in unsupported environments
    }
  }
}
